/* ==========================================================================
   ASK A VET™ — Animal Water Intake Calculator
   File: assets/s-water-intake-calculator.js
   ========================================================================== */

(() => {
  'use strict';

  const SPECIES = {
    dog: {
      label: 'Dog',
      low: 50,
      high: 70,
      energy(weightKg) {
        return 112 * Math.pow(weightKg, 0.75);
      },
      dmKcal: 4.1,
      dryM: 0.1,
      wetM: 0.78,
      dryLabel: 'Dry kibble',
      wetLabel: 'Wet / canned / raw',
      placeholder: 'e.g. 20',
      polyPerKg: 100,
      note:
        "Dogs regulate thirst well. Watch for a sudden, persistent jump in drinking over several days: that pattern is a classic early sign of kidney disease, diabetes or Cushing's disease and is worth a vet check with a fresh urine sample.",
    },

    cat: {
      label: 'Cat',
      low: 40,
      high: 60,
      energy(weightKg) {
        return 84 * Math.pow(weightKg, 0.75);
      },
      dmKcal: 4.2,
      dryM: 0.08,
      wetM: 0.78,
      dryLabel: 'Dry biscuits',
      wetLabel: 'Wet / canned / raw',
      placeholder: 'e.g. 4.5',
      polyPerKg: 100,
      note:
        'Cats are desert-adapted and naturally poor drinkers. On a mostly wet diet a cat drinking very little is normal. On a fully dry diet, encourage drinking with water fountains, wide shallow bowls and multiple stations. Increased thirst in an older cat is never normal: kidney disease, hyperthyroidism and diabetes top the list.',
    },

    rabbit: {
      label: 'Rabbit',
      low: 80,
      high: 150,
      dmPerKg: 35,
      dryM: 0.1,
      wetM: 0.82,
      dryLabel: 'Hay & pellets',
      wetLabel: 'Fresh greens & veg',
      placeholder: 'e.g. 2.5',
      note:
        'Rabbits drink a surprising amount for their size, often more per kg than a dog. Bowls beat bottles: rabbits drink more from an open bowl. A rabbit that stops drinking and eating for 12 to 24 hours is an emergency (gut stasis).',
    },

    'guinea-pig': {
      label: 'Guinea pig',
      low: 60,
      high: 100,
      dmPerKg: 40,
      dryM: 0.1,
      wetM: 0.85,
      dryLabel: 'Hay & pellets',
      wetLabel: 'Fresh greens & veg',
      placeholder: 'e.g. 1.0',
      note:
        'Guinea pigs on plenty of fresh veg drink less, and that is fine. Check bottle spouts daily: a jammed ball bearing quietly dehydrates them. Any guinea pig not eating or drinking for 12 to 24 hours needs a vet.',
    },

    ferret: {
      label: 'Ferret',
      low: 75,
      high: 100,
      energy(weightKg) {
        return 250 * weightKg;
      },
      dmKcal: 4.6,
      dryM: 0.08,
      wetM: 0.7,
      dryLabel: 'Dry kibble',
      wetLabel: 'Raw / wet food',
      placeholder: 'e.g. 1.2',
      note:
        'Ferrets on raw or whole-prey diets get most of their water from food and may drink little. Ferrets on dry kibble must have constant water access, they dehydrate quickly.',
    },

    'companion-bird': {
      label: 'Bird (companion)',
      low: 80,
      high: 150,
      dmPerKg: 100,
      dryM: 0.1,
      wetM: 0.85,
      dryLabel: 'Seed & pellets',
      wetLabel: 'Fresh fruit & veg',
      placeholder: 'e.g. 0.4',
      note:
        'Small birds drink a lot per gram of body weight and can dehydrate within a day if water is spilled or fouled. Birds on lots of fruit and veg drink less and produce wetter droppings, which is normal. Replace water daily, twice daily in summer.',
    },

    chicken: {
      label: 'Chicken / poultry',
      low: 90,
      high: 180,
      dmPerKg: 45,
      dryM: 0.1,
      wetM: 0.8,
      dryLabel: 'Mash / grain',
      wetLabel: 'Greens & scraps',
      placeholder: 'e.g. 2.5',
      note:
        'Laying hens drink roughly twice the weight of the feed they eat, and egg production drops fast if water runs out even briefly. In hot weather demand can double. One drinker is never enough for a flock: dominant birds guard them.',
    },

    pig: {
      label: 'Pig',
      low: 60,
      high: 110,
      dmPerKg: 30,
      dryM: 0.12,
      wetM: 0.72,
      dryLabel: 'Dry feed',
      wetLabel: 'Wet / liquid feed',
      placeholder: 'e.g. 80',
      note:
        'Pigs on restricted water develop salt poisoning (water deprivation sodium toxicity), which is fatal and fast. Nipple drinker flow rates matter as much as volume. Lactating sows can need 25 to 40 litres a day.',
    },

    sheep: {
      label: 'Sheep',
      low: 50,
      high: 90,
      dmPerKg: 25,
      dryM: 0.1,
      wetM: 0.8,
      dryLabel: 'Hay / dry feed',
      wetLabel: 'Fresh pasture',
      placeholder: 'e.g. 70',
      note:
        'Sheep on lush green pasture may drink very little, and on dry summer feed their trough demand jumps sharply. Lactating ewes can need two to three times maintenance.',
    },

    goat: {
      label: 'Goat',
      low: 50,
      high: 90,
      dmPerKg: 25,
      dryM: 0.1,
      wetM: 0.8,
      dryLabel: 'Hay / dry feed',
      wetLabel: 'Fresh pasture / browse',
      placeholder: 'e.g. 60',
      note:
        'Goats are fussy drinkers and will go thirsty rather than drink dirty or very cold water, which raises urinary blockage risk in wethers and bucks. Clean troughs matter as much as full ones.',
    },

    cattle: {
      label: 'Cattle',
      low: 50,
      high: 90,
      dmPerKg: 22,
      dryM: 0.1,
      wetM: 0.8,
      dryLabel: 'Hay / silage / grain',
      wetLabel: 'Fresh pasture',
      placeholder: 'e.g. 500',
      lactX: 2.5,
      note:
        'A lactating dairy cow is in a league of her own: roughly 4 to 5 litres of water for every litre of milk produced, so high producers can drink 100 to 150+ litres a day. Trough space and flow rate limit intake before thirst does.',
    },

    horse: {
      label: 'Horse',
      low: 45,
      high: 70,
      dmPerKg: 20,
      dryM: 0.1,
      wetM: 0.8,
      dryLabel: 'Hay / hard feed',
      wetLabel: 'Fresh pasture',
      placeholder: 'e.g. 500',
      note:
        'A 500 kg horse typically drinks 25 to 35 litres a day on hay, and far less on lush pasture. Reduced drinking on an all-hay diet is a classic setup for impaction colic, especially in cold weather when water is icy. Heavy sweating adds 10 to 15 litres of demand.',
    },
  };

  const STAGE_MULTIPLIERS = {
    adult: 1,
    growing: 1.2,
    pregnant: 1.3,
    lactating: 1.8,
    senior: 1,
  };

  const ACTIVITY_MULTIPLIERS = {
    low: 0.9,
    normal: 1,
    high: 1.3,
  };

  const WEATHER_MULTIPLIERS = {
    mild: 1,
    warm: 1.25,
    hot: 1.5,
  };

  const KG_PER_LB = 1 / 2.20462;
  const ML_PER_LITRE = 1000;
  const ML_PER_METRIC_CUP = 250;

  class WaterIntakeCalculator extends HTMLElement {
    constructor() {
      super();

      this.initialised = false;
      this.lastFocusedElement = null;

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleSpeciesChange = this.handleSpeciesChange.bind(this);
      this.handleDietInput = this.handleDietInput.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    }

    connectedCallback() {
      if (this.initialised) {
        return;
      }

      this.cacheElements();

      if (!this.form || !this.speciesInput || !this.weightInput) {
        return;
      }

      this.initialised = true;

      this.bindEvents();
      this.applyDefaultSpecies();
      this.updateSpeciesFields();
      this.updateDietReadout();
    }

    disconnectedCallback() {
      if (!this.initialised) {
        return;
      }

      this.unbindEvents();
      this.closeModal(false);
      this.initialised = false;
    }

    cacheElements() {
      this.form = this.querySelector('[data-wic-form]');
      this.speciesInput = this.querySelector('[data-wic-species]');
      this.weightInput = this.querySelector('[data-wic-weight]');
      this.weightUnitInput = this.querySelector('[data-wic-weight-unit]');
      this.lifeStageInput = this.querySelector('[data-wic-life-stage]');
      this.activityInput = this.querySelector('[data-wic-activity]');
      this.weatherInput = this.querySelector('[data-wic-weather]');
      this.actualIntakeInput = this.querySelector('[data-wic-actual-intake]');
      this.actualUnitInput = this.querySelector('[data-wic-actual-unit]');
      this.dietInput = this.querySelector('[data-wic-diet]');
      this.dietReadout = this.querySelector('[data-wic-diet-readout]');
      this.dryLabel = this.querySelector('[data-wic-dry-label]');
      this.wetLabel = this.querySelector('[data-wic-wet-label]');
      this.errorElement = this.querySelector('[data-wic-error]');

      this.modal = this.querySelector('[data-wic-modal]');
      this.modalCard = this.querySelector('[data-wic-modal-card]');
      this.closeButtons = Array.from(
        this.querySelectorAll('[data-wic-close]')
      );

      this.resultLabel = this.querySelector('[data-wic-result-label]');
      this.resultTotal = this.querySelector('[data-wic-result-total]');
      this.resultText = this.querySelector('[data-wic-result-text]');
      this.resultReport = this.querySelector('[data-wic-result-report]');
      this.verdict = this.querySelector('[data-wic-verdict]');
      this.recommendations = this.querySelector(
        '[data-wic-recommendations]'
      );
    }

    bindEvents() {
      this.form.addEventListener('submit', this.handleSubmit);
      this.speciesInput.addEventListener(
        'change',
        this.handleSpeciesChange
      );
      this.dietInput?.addEventListener('input', this.handleDietInput);

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.handleCloseClick);
      });

      document.addEventListener('keydown', this.handleDocumentKeydown);
    }

    unbindEvents() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.speciesInput?.removeEventListener(
        'change',
        this.handleSpeciesChange
      );
      this.dietInput?.removeEventListener('input', this.handleDietInput);

      this.closeButtons?.forEach((button) => {
        button.removeEventListener('click', this.handleCloseClick);
      });

      document.removeEventListener('keydown', this.handleDocumentKeydown);
    }

    applyDefaultSpecies() {
      const defaultSpecies = this.dataset.defaultSpecies;

      if (
        defaultSpecies &&
        SPECIES[defaultSpecies] &&
        this.speciesInput.querySelector(
          `option[value="${CSS.escape(defaultSpecies)}"]`
        )
      ) {
        this.speciesInput.value = defaultSpecies;
      }
    }

    getSpecies() {
      return SPECIES[this.speciesInput.value] || SPECIES.dog;
    }

    handleSpeciesChange() {
      this.updateSpeciesFields();
      this.updateDietReadout();
      this.clearError();
    }

    handleDietInput() {
      this.updateDietReadout();
    }

    handleCloseClick() {
      this.closeModal();
    }

    updateSpeciesFields() {
      const species = this.getSpecies();

      this.weightInput.placeholder = species.placeholder;

      if (this.dryLabel) {
        this.dryLabel.textContent = species.dryLabel;
      }

      if (this.wetLabel) {
        this.wetLabel.textContent = species.wetLabel;
      }
    }

    updateDietReadout() {
      if (!this.dietInput || !this.dietReadout) {
        return;
      }

      const wetPercentage = this.clamp(
        Number.parseInt(this.dietInput.value, 10) || 0,
        0,
        100
      );

      this.dietReadout.textContent =
        `${100 - wetPercentage}% dry / ${wetPercentage}% wet`;
    }

    handleSubmit(event) {
      event.preventDefault();
      this.clearError();

      const weightInputValue = Number.parseFloat(this.weightInput.value);

      if (!Number.isFinite(weightInputValue) || weightInputValue <= 0) {
        this.showError('Please enter a valid body weight.');
        this.weightInput.setAttribute('aria-invalid', 'true');
        this.weightInput.focus();
        return;
      }

      this.weightInput.removeAttribute('aria-invalid');

      let weightKg = weightInputValue;

      if (this.weightUnitInput.value === 'lb') {
        weightKg *= KG_PER_LB;
      }

      const species = this.getSpecies();
      const stage = this.lifeStageInput.value;

      let stageMultiplier = STAGE_MULTIPLIERS[stage] || 1;

      if (stage === 'lactating' && species.lactX) {
        stageMultiplier = species.lactX;
      }

      const activityMultiplier =
        ACTIVITY_MULTIPLIERS[this.activityInput.value] || 1;

      const weatherMultiplier =
        WEATHER_MULTIPLIERS[this.weatherInput.value] || 1;

      const totalMultiplier =
        stageMultiplier * activityMultiplier * weatherMultiplier;

      /*
       * Total requirement range, including water contained in food.
       */
      const needLow =
        species.low * weightKg * totalMultiplier;

      const needHigh =
        species.high * weightKg * totalMultiplier;

      /*
       * Water contained in food.
       *
       * Water in food =
       * dry matter × (
       *   dry fraction × dry moisture / (1 - dry moisture)
       *   +
       *   wet fraction × wet moisture / (1 - wet moisture)
       * )
       */
      const wetFraction =
        this.clamp(
          Number.parseInt(this.dietInput.value, 10) || 0,
          0,
          100
        ) / 100;

      let dryMatterGrams;

      if (typeof species.energy === 'function') {
        dryMatterGrams =
          species.energy(weightKg) / species.dmKcal;
      } else {
        dryMatterGrams =
          species.dmPerKg * weightKg;
      }

      /*
       * Growing, pregnant and lactating animals also eat more.
       */
      dryMatterGrams *= stageMultiplier;

      const dryFoodWaterRatio =
        species.dryM / (1 - species.dryM);

      const wetFoodWaterRatio =
        species.wetM / (1 - species.wetM);

      const foodWater =
        dryMatterGrams *
        (
          (1 - wetFraction) * dryFoodWaterRatio +
          wetFraction * wetFoodWaterRatio
        );

      const drinkLow = Math.max(0, needLow - foodWater);
      const drinkHigh = Math.max(0, needHigh - foodWater);

      this.renderResult({
        species,
        weightKg,
        needLow,
        needHigh,
        foodWater,
        drinkLow,
        drinkHigh,
        wetFraction,
        totalMultiplier,
      });

      this.openModal();
    }

    renderResult({
      species,
      weightKg,
      needLow,
      needHigh,
      foodWater,
      drinkLow,
      drinkHigh,
      wetFraction,
      totalMultiplier,
    }) {
      if (this.resultLabel) {
        this.resultLabel.textContent =
          `${species.label} water intake result`;
      }

      if (this.resultTotal) {
        this.resultTotal.textContent =
          this.formatShortRange(needLow, needHigh);
      }

      if (this.resultText) {
        this.resultText.textContent =
          `A ${this.roundToOneDecimal(weightKg)} kg ` +
          `${species.label.toLowerCase()} on this diet and routine ` +
          `needs roughly ${this.formatRange(needLow, needHigh)} ` +
          `of water per day in total ` +
          `(${Math.round(species.low * totalMultiplier)} to ` +
          `${Math.round(species.high * totalMultiplier)} mL per kg), ` +
          'counting both food and drinking.';
      }

      this.renderBreakdown({
        needLow,
        foodWater,
        drinkLow,
        drinkHigh,
        wetFraction,
      });

      this.renderMeasuredIntakeVerdict({
        species,
        weightKg,
        drinkLow,
        drinkHigh,
      });

      if (this.recommendations) {
        this.recommendations.textContent = species.note;
      }

      this.dispatchEvent(
        new CustomEvent('water-intake:calculated', {
          bubbles: true,
          detail: {
            species: this.speciesInput.value,
            weightKg,
            needLowMl: needLow,
            needHighMl: needHigh,
            foodWaterMl: foodWater,
            drinkLowMl: drinkLow,
            drinkHighMl: drinkHigh,
            wetFoodPercentage: Math.round(wetFraction * 100),
          },
        })
      );
    }

    renderBreakdown({
      needLow,
      foodWater,
      drinkLow,
      drinkHigh,
      wetFraction,
    }) {
      if (!this.resultReport) {
        return;
      }

      const items = [];

      const foodItem = document.createElement('li');
      const foodHeading = document.createElement('strong');

      foodHeading.textContent = 'Water from food:';
      foodItem.append(foodHeading, ' ');

      if (wetFraction === 0) {
        foodItem.append(
          `${this.formatVolume(foodWater)} ` +
          '(a fully dry diet supplies almost nothing)'
        );
      } else {
        foodItem.append(
          `${this.formatVolume(foodWater)} ` +
          `(from the ${Math.round(wetFraction * 100)}% ` +
          'wet/fresh share of the diet)'
        );
      }

      items.push(foodItem);

      const drinkingItem = document.createElement('li');
      const drinkingHeading = document.createElement('strong');
      const cups = this.formatCups(drinkLow, drinkHigh);

      drinkingHeading.textContent =
        'Should drink from the bowl or trough:';

      drinkingItem.append(
        drinkingHeading,
        ` ${this.formatRange(drinkLow, drinkHigh)}`
      );

      if (cups) {
        drinkingItem.append(`, ${cups}`);
      }

      items.push(drinkingItem);

      if (foodWater >= needLow && wetFraction > 0) {
        const wetDietItem = document.createElement('li');
        const wetDietHeading = document.createElement('strong');

        wetDietHeading.textContent = 'Wet diet note:';

        wetDietItem.append(
          wetDietHeading,
          ' on this diet split, food alone can cover most or all ' +
          'of the requirement, so your animal may drink very little. ' +
          'That is normal. Fresh water must still be available at all times.'
        );

        items.push(wetDietItem);
      }

      this.resultReport.replaceChildren(...items);
    }

    renderMeasuredIntakeVerdict({
      species,
      weightKg,
      drinkLow,
      drinkHigh,
    }) {
      if (!this.verdict) {
        return;
      }

      const rawActualValue = this.actualIntakeInput.value.trim();

      if (rawActualValue === '') {
        this.verdict.replaceChildren();
        return;
      }

      let actual = Number.parseFloat(rawActualValue);

      if (!Number.isFinite(actual) || actual < 0) {
        this.verdict.replaceChildren();
        return;
      }

      switch (this.actualUnitInput.value) {
        case 'l':
          actual *= ML_PER_LITRE;
          break;

        case 'cups':
          actual *= ML_PER_METRIC_CUP;
          break;

        default:
          break;
      }

      let verdictClass;
      let verdictHtml;

      const polyThreshold = species.polyPerKg
        ? species.polyPerKg * weightKg
        : drinkHigh * 1.5;

      const polyLine = species.polyPerKg
        ? `For ${species.label.toLowerCase()}s, sustained total intake ` +
          `above about ${Math.round(species.polyPerKg)} mL per kg per day ` +
          'is considered excessive.'
        : 'There is no single diagnostic cut-off for this species, ' +
          'so the trend and the animal’s condition matter most.';

      if (
        actual > Math.max(drinkHigh * 1.5, polyThreshold)
      ) {
        verdictClass = 'bad';

        verdictHtml =
          '<b>Drinking looks high.</b> ' +
          `Measured intake is ${this.formatVolume(actual)}, well above ` +
          `the expected ${this.formatRange(drinkLow, drinkHigh)} ` +
          'from the bowl or trough for this diet. ' +
          polyLine +
          ' If this is a real, sustained change rather than a one-off hot ' +
          'or active day, arrange a vet check and take a fresh urine sample. ' +
          'Do not restrict water while you wait.';
      } else if (
        drinkLow > 0 &&
        actual < drinkLow * 0.5
      ) {
        verdictClass = 'warn';

        verdictHtml =
          '<b>Drinking looks low.</b> ' +
          'Measured intake is well under the expected range for this diet. ' +
          'First check the maths: other water sources (ponds, puddles, ' +
          'toilets, second bowls, rain on troughs) and food moisture often ' +
          'explain it. If intake is truly low, or your animal is flat, ' +
          'off food, vomiting or has tacky gums, see a vet promptly. ' +
          'Small animals and young animals dehydrate dangerously fast.';
      } else {
        verdictClass = 'good';

        verdictHtml =
          '<b>Drinking is within the expected range.</b> ' +
          polyLine +
          ' Keep an eye on the trend rather than any single day: ' +
          'a sustained change of more than about 50% in either direction ' +
          'is worth investigating.';
      }

      const verdictElement = document.createElement('div');

      verdictElement.className =
        `water-intake-calculator__verdict ` +
        `water-intake-calculator__verdict--${verdictClass}`;

      verdictElement.innerHTML = verdictHtml;

      this.verdict.replaceChildren(verdictElement);
    }

    showError(message) {
      if (!this.errorElement) {
        return;
      }

      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    clearError() {
      if (!this.errorElement) {
        return;
      }

      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
      this.weightInput?.removeAttribute('aria-invalid');
    }

    openModal() {
      if (!this.modal) {
        return;
      }

      this.lastFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');

      document.documentElement.classList.add('wic-modal-open');
      document.body.classList.add('wic-modal-open');

      window.requestAnimationFrame(() => {
        this.modalCard?.focus();
      });
    }

    closeModal(restoreFocus = true) {
      if (!this.modal || this.modal.hidden) {
        return;
      }

      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');

      document.documentElement.classList.remove('wic-modal-open');
      document.body.classList.remove('wic-modal-open');

      if (restoreFocus && this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }

      this.lastFocusedElement = null;
    }

    handleDocumentKeydown(event) {
      if (!this.modal || this.modal.hidden) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key === 'Tab') {
        this.trapModalFocus(event);
      }
    }

    trapModalFocus(event) {
      if (!this.modalCard) {
        return;
      }

      const focusableElements = Array.from(
        this.modalCard.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
          ].join(',')
        )
      ).filter((element) => {
        return (
          element instanceof HTMLElement &&
          !element.hidden &&
          element.offsetParent !== null
        );
      });

      if (!focusableElements.length) {
        event.preventDefault();
        this.modalCard.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    formatVolume(ml) {
      if (ml >= 10000) {
        const litres = ml / 1000;
        const decimals = ml >= 100000 ? 0 : 1;

        return `${litres
          .toFixed(decimals)
          .replace(/\.0$/, '')} L`;
      }

      if (ml >= 1000) {
        return `${(ml / 1000)
          .toFixed(2)
          .replace(/0$/, '')
          .replace(/\.$/, '')} L`;
      }

      return `${Math.round(ml)} mL`;
    }

    formatRange(low, high) {
      if (Math.round(low) === Math.round(high)) {
        return this.formatVolume(low);
      }

      return `${this.formatVolume(low)} to ${this.formatVolume(high)}`;
    }

    formatShortRange(low, high) {
      if (high >= 1000) {
        const formatLitres = (ml) => {
          const litres = ml / 1000;

          return litres >= 10
            ? Math.round(litres)
            : Math.round(litres * 10) / 10;
        };

        const lowLitres = formatLitres(low);
        const highLitres = formatLitres(high);

        if (lowLitres === highLitres) {
          return `${lowLitres} L`;
        }

        return `${lowLitres}-${highLitres} L`;
      }

      const roundedLow = Math.round(low);
      const roundedHigh = Math.round(high);

      if (roundedLow === roundedHigh) {
        return `${roundedLow} mL`;
      }

      return `${roundedLow}-${roundedHigh} mL`;
    }

    formatCups(low, high) {
      const lowCups = low / ML_PER_METRIC_CUP;
      const highCups = high / ML_PER_METRIC_CUP;

      if (highCups < 0.25) {
        return '';
      }

      const roundCups = (value) => {
        if (value >= 10) {
          return Math.round(value);
        }

        return Math.round(value * 2) / 2;
      };

      const roundedLow = roundCups(lowCups);
      const roundedHigh = roundCups(highCups);

      const amount =
        roundedLow === roundedHigh
          ? String(roundedLow)
          : `${roundedLow} to ${roundedHigh}`;

      const unit =
        roundedHigh > 1
          ? 'metric cups'
          : 'metric cup';

      return `about ${amount} ${unit} (250 mL)`;
    }

    roundToOneDecimal(value) {
      return Math.round(value * 10) / 10;
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }
  }

  if (!customElements.get('s-water-intake-calculator')) {
    customElements.define(
      's-water-intake-calculator',
      WaterIntakeCalculator
    );
  }
})();
